import React, { useEffect, useState } from 'react';
import {
  addPortalMember, fetchPortalSessions, invitePortalMember, removePortalMember, revokePortalSession,
  signOutPortalPerson, updatePortalMember,
} from '../api';
import { deviceName } from '../../utils/deviceName';
import { errorText, when } from './AccountEditor';

// The people at this business who can sign in. The same email in two businesses is one
// person with one sign-in who switches between them on the website.

const blankPerson = (options) => ({
  name: '', email: '', phone: '', title: '',
  permissions: options.features.map((f) => f.key),
  view_scope: 'all', job_emails: 'own', send_invite: true,
});

function PermissionBoxes({ value, onChange, options, account }) {
  return (
    <div className="pa-chips">
      {options.features.map((f) => {
        const accountHas = account.features.includes(f.key);
        return (
          <label key={f.key} className={`pa-chip ${accountHas ? '' : 'pa-chip-off'}`}
                 title={accountHas ? f.help : 'Turned off for this business (Settings)'}>
            <input type="checkbox" checked={value.includes(f.key)}
                   onChange={(e) => onChange(e.target.checked ? [...value, f.key] : value.filter((k) => k !== f.key))} />
            {f.label}
          </label>
        );
      })}
    </div>
  );
}

function ChoiceSelect({ value, onChange, choices }) {
  return (
    <select className="pa-input" value={value} onChange={(e) => onChange(e.target.value)}>
      {choices.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
    </select>
  );
}

function AddPerson({ account, options, onAdded, onCancel }) {
  const [draft, setDraft] = useState(() => blankPerson(options));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (key, value) => setDraft((d) => ({ ...d, [key]: value }));

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      const added = await addPortalMember(account.id, draft);
      onAdded(added);
    } catch (err) {
      setError(errorText(err, 'Could not add this person.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="pa-card pa-person-form" onSubmit={save}>
      <h4>Add a person</h4>
      <div className="pa-row">
        <label className="pa-field"><span>Name</span>
          <input className="pa-input" value={draft.name} onChange={(e) => set('name', e.target.value)} autoFocus />
        </label>
        <label className="pa-field"><span>Email (they sign in with this)</span>
          <input className="pa-input" type="email" required value={draft.email} onChange={(e) => set('email', e.target.value)} />
        </label>
      </div>
      <div className="pa-row">
        <label className="pa-field"><span>Phone</span>
          <input className="pa-input" value={draft.phone} onChange={(e) => set('phone', e.target.value)} />
        </label>
        <label className="pa-field"><span>Title</span>
          <input className="pa-input" placeholder="e.g. Service advisor" value={draft.title} onChange={(e) => set('title', e.target.value)} />
        </label>
      </div>
      <span className="pa-label">Can</span>
      <PermissionBoxes value={draft.permissions} onChange={(v) => set('permissions', v)} options={options} account={account} />
      <div className="pa-row">
        <label className="pa-field"><span>Sees</span>
          <ChoiceSelect value={draft.view_scope} onChange={(v) => set('view_scope', v)} choices={options.view_scopes} />
        </label>
        <label className="pa-field"><span>Job emails</span>
          <ChoiceSelect value={draft.job_emails} onChange={(v) => set('job_emails', v)} choices={options.job_emails} />
        </label>
      </div>
      <label className="pa-check">
        <input type="checkbox" checked={draft.send_invite} onChange={(e) => set('send_invite', e.target.checked)} />
        <span className="pa-check-label">Email them an invite with the link</span>
      </label>
      {error && <div className="pa-error">{error}</div>}
      <div className="pa-actions">
        <button type="submit" className="pa-button" disabled={saving}>{saving ? 'Adding…' : 'Add person'}</button>
        <button type="button" className="pa-link" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

function Devices({ person }) {
  const [sessions, setSessions] = useState(null);
  const [error, setError] = useState('');
  const load = () => fetchPortalSessions(person.id).then(setSessions).catch(() => setError('Could not load devices.'));
  useEffect(() => { load(); }, [person.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const revoke = async (id) => {
    try { await revokePortalSession(id); load(); } catch { setError('Could not sign that device out.'); }
  };
  const revokeAll = async () => {
    try { await signOutPortalPerson(person.id); load(); } catch { setError('Could not sign them out.'); }
  };

  if (error) return <div className="pa-error">{error}</div>;
  if (!sessions) return <div className="pa-muted">Loading devices…</div>;
  const active = sessions.filter((s) => s.active);
  return (
    <div className="pa-devices">
      <div className="pa-devices-header">
        <span className="pa-label">Signed-in devices ({active.length})</span>
        {active.length > 0 && <button type="button" className="pa-link" onClick={revokeAll}>Sign out everywhere</button>}
      </div>
      {active.length === 0 && <div className="pa-muted">Not signed in anywhere.</div>}
      <ul>
        {active.map((s) => (
          <li key={s.id}>
            <span className="pa-device-name" title={s.device}>{deviceName(s.device)}</span>
            <span className="pa-muted">
              Signed in {when(s.created_at)} · last used {when(s.last_used_at)}
              {s.last_ip ? ` from ${s.last_ip}` : ''}
              {s.remember ? ` · stays signed in until ${when(s.expires_at)} unless used` : ' · this visit only'}
            </span>
            <button type="button" className="pa-link" onClick={() => revoke(s.id)}>Sign out</button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Member({ member, account, options, onChanged }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [confirmRemove, setConfirmRemove] = useState(false);
  const p = member.person;

  const startEdit = () => {
    setDraft({
      permissions: member.permissions, view_scope: member.view_scope, job_emails: member.job_emails,
      is_active: member.is_active,
      person: { name: p.name, email: p.email, phone: p.phone, title: p.title, is_active: p.is_active },
    });
    setOpen(true);
    setMessage('');
  };

  const save = async () => {
    setSaving(true);
    try {
      await updatePortalMember(member.id, draft);
      setMessage('Saved.');
      onChanged();
    } catch (err) {
      setMessage(errorText(err, 'Could not save.'));
    } finally {
      setSaving(false);
    }
  };

  const invite = async () => {
    try { await invitePortalMember(member.id); setMessage(`Invite emailed to ${p.email}.`); } catch (err) {
      setMessage(errorText(err, 'Could not send the invite.'));
    }
  };

  const remove = async () => {
    try { await removePortalMember(member.id); onChanged(); } catch (err) { setMessage(errorText(err, 'Could not remove.')); }
  };

  const setPerson = (key, value) => setDraft((d) => ({ ...d, person: { ...d.person, [key]: value } }));
  const status = !p.is_active ? 'Turned off' : !member.is_active ? 'No access here' : null;

  return (
    <li className={`pa-member ${status ? 'pa-member-off' : ''}`}>
      <button type="button" className="pa-member-summary" onClick={() => (open ? setOpen(false) : startEdit())}>
        <span className="pa-member-name">{p.name}{p.title ? <span className="pa-muted"> · {p.title}</span> : null}</span>
        <span className="pa-muted">{p.email}</span>
        <span className="pa-member-meta">
          {status && <span className="pa-tag pa-tag-off">{status}</span>}
          {member.signed_in_devices > 0 ? `${member.signed_in_devices} device${member.signed_in_devices === 1 ? '' : 's'}` : 'Not signed in'}
          {' · '}last sign-in {p.last_sign_in_at ? when(p.last_sign_in_at) : 'never'}
        </span>
      </button>
      {open && draft && (
        <div className="pa-member-edit">
          <div className="pa-row">
            <label className="pa-field"><span>Name</span>
              <input className="pa-input" value={draft.person.name} onChange={(e) => setPerson('name', e.target.value)} />
            </label>
            <label className="pa-field"><span>Email</span>
              <input className="pa-input" type="email" value={draft.person.email} onChange={(e) => setPerson('email', e.target.value)} />
            </label>
          </div>
          <div className="pa-row">
            <label className="pa-field"><span>Phone</span>
              <input className="pa-input" value={draft.person.phone} onChange={(e) => setPerson('phone', e.target.value)} />
            </label>
            <label className="pa-field"><span>Title</span>
              <input className="pa-input" value={draft.person.title} onChange={(e) => setPerson('title', e.target.value)} />
            </label>
          </div>
          {p.other_accounts.length > 0 && (
            <p className="pa-help">Also has the portal for: {p.other_accounts.join(', ')}. Name, email and phone are shared.</p>
          )}
          <span className="pa-label">Can</span>
          <PermissionBoxes value={draft.permissions} onChange={(v) => setDraft((d) => ({ ...d, permissions: v }))}
                           options={options} account={account} />
          <div className="pa-row">
            <label className="pa-field"><span>Sees</span>
              <ChoiceSelect value={draft.view_scope} onChange={(v) => setDraft((d) => ({ ...d, view_scope: v }))} choices={options.view_scopes} />
            </label>
            <label className="pa-field"><span>Job emails</span>
              <ChoiceSelect value={draft.job_emails} onChange={(v) => setDraft((d) => ({ ...d, job_emails: v }))} choices={options.job_emails} />
            </label>
          </div>
          <label className="pa-check">
            <input type="checkbox" checked={draft.is_active} onChange={(e) => setDraft((d) => ({ ...d, is_active: e.target.checked }))} />
            <span className="pa-check-label">Has access to {account.customer_name}</span>
          </label>
          <label className="pa-check">
            <input type="checkbox" checked={draft.person.is_active} onChange={(e) => setPerson('is_active', e.target.checked)} />
            <span>
              <span className="pa-check-label">Can sign in at all</span>
              <span className="pa-help">Off signs them out everywhere, for every business.</span>
            </span>
          </label>
          <div className="pa-actions">
            <button type="button" className="pa-button" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save'}</button>
            <button type="button" className="pa-button pa-button-secondary" onClick={invite}>Email invite</button>
            {confirmRemove ? (
              <span className="pa-confirm">
                Remove {p.name} from {account.customer_name}?
                <button type="button" className="pa-link pa-danger" onClick={remove}>Remove</button>
                <button type="button" className="pa-link" onClick={() => setConfirmRemove(false)}>Keep</button>
              </span>
            ) : (
              <button type="button" className="pa-link pa-danger" onClick={() => setConfirmRemove(true)}>Remove from this business</button>
            )}
            {message && <span className="pa-message">{message}</span>}
          </div>
          <Devices person={p} />
        </div>
      )}
    </li>
  );
}

function People({ account, options, onChanged }) {
  const [adding, setAdding] = useState(account.members.length === 0);
  const [notice, setNotice] = useState('');

  const added = (member) => {
    setAdding(false);
    setNotice(member.invited ? `Added ${member.person.name} and emailed them an invite.` : `Added ${member.person.name}.`);
    onChanged();
  };

  return (
    <div>
      {notice && <div className="pa-notice">{notice}</div>}
      {adding ? (
        <AddPerson account={account} options={options} onAdded={added} onCancel={() => setAdding(false)} />
      ) : (
        <button type="button" className="pa-button" onClick={() => { setAdding(true); setNotice(''); }}>Add a person</button>
      )}
      <ul className="pa-members">
        {account.members.map((m) => (
          <Member key={m.id} member={m} account={account} options={options} onChanged={onChanged} />
        ))}
      </ul>
    </div>
  );
}

export default People;
